import React from 'react';
import { PropertyList } from '../../../components/Redesing/property-list';
import { Divider } from '@mui/material';
import { PropertyListItem } from '../../../components/Redesing/property-list-item';
import { IMembership } from '../../../ts/interface/membership.interface';
import { IImage } from '../../../ts/interface/image.interface';
import { ISystem } from '../../../ts/interface/system.interface';
import { convertCentsToMoney, timeForHumans } from '../../../utils';
import NoFilePlaceHolder from '../../../components/Redesing/no-file-place-holder';

/**
 * How a plan relates to the plans around it, from its replacement pointer.
 *
 * The schema (and ant's replaceMembership) points old -> new: an archived plan
 * A with `replacementMembership` B reads "Replaced by B", and B finds A
 * through `replacesNames` (plans with an archive date whose
 * replacementMembershipOldId is B).
 * The legacy Python `p_add_membership` wrote the pointer the other way
 * (new -> old) on a plan that stays live, so a pointer on a plan with no
 * archive date is read as "Replaces".
 */
export function replacementLabels(
  membership: Pick<IMembership, 'archived_on' | 'membership_replaced'> | undefined,
  replacesNames: string[] = []
): { replacedBy: string | null; replaces: string[] } {
  const pointer = membership?.membership_replaced ?? null;
  const replacedBy = pointer && membership?.archived_on ? pointer : null;
  const replaces = [...replacesNames];
  if (pointer && !membership?.archived_on && !replaces.includes(pointer)) {
    replaces.push(pointer);
  }
  return { replacedBy, replaces };
}

function MembershipCardDetails({
  membership,
  membershipImages,
  currentSystem,
  showCurrentSystem = false,
  replacesNames = [],
}: {
  membership: IMembership;
  membershipImages: IImage[];
  currentSystem: ISystem;
  showCurrentSystem?: boolean;
  /** Names of the plans this one replaced (they point at it). */
  replacesNames?: string[];
}) {
  const { replacedBy, replaces } = replacementLabels(membership, replacesNames);
  const systemUsesWallets = currentSystem.billing && currentSystem.wallet_required;
  const logo = membershipImages?.find(
    (image: IImage) =>
      image.id === membership?.logo_image_id &&
      (image.type === 'MembershipLogo' || image.type === 'SystemLogo')
  );

  const free = !membership?.prices?.length;
  const minimum_wallet_deposit = Number.isInteger(
    Number(membership?.minimum_wallet_deposit)
  )
    ? convertCentsToMoney(Number(membership.minimum_wallet_deposit))
    : null;
  const credit_card_hold = Number.isInteger(membership?.credit_card_hold)
    ? convertCentsToMoney(membership.credit_card_hold)
    : null;

  return (
    <PropertyList>
      {showCurrentSystem && (
        <>
          <Divider />
          <PropertyListItem
            label="System"
            value={membership?.system?.name}
            dataId="current_system"
          />
        </>
      )}
      {free ? (
        <>
          <Divider />
          <PropertyListItem
            label="Cost"
            value="This is a free membership"
            dataId="cost"
          />
        </>
      ) : null}
      <Divider />
      <PropertyListItem
        label="Active On"
        value={new Date(membership?.active_on as string).toDateString()}
        dataId="active-on"
      />
      {membership?.archived_on ? (
        <>
          <Divider />
          <PropertyListItem
            label="Archived On"
            value={new Date(membership.archived_on).toDateString()}
          />
        </>
      ) : null}
      {replacedBy ? (
        <>
          <Divider />
          <PropertyListItem label="Replaced by" value={replacedBy} dataId="replaced-by" />
        </>
      ) : null}
      {replaces.length ? (
        <>
          <Divider />
          <PropertyListItem
            label="Replaces"
            value={replaces.join(', ')}
            dataId="replaces"
          />
        </>
      ) : null}
      {membership?.auth_type ? (
        <>
          <Divider />
          <PropertyListItem
            label="Authorization Type"
            value={membership.auth_type}
            dataId="auth-type"
          />
        </>
      ) : null}
      {membership?.auth_type === 'email' ? (
        <>
          <Divider />
          <PropertyListItem
            divider
            label="Valid Emails"
            value={
              membership.valid_emails.length
                ? membership.valid_emails.join(', ')
                : 'All Domains Accepted'
            }
            dataId="valid-emails"
          />
          <PropertyListItem
            label="Hide Valid Emails"
            value={membership.hide_valid_emails ? 'Yes' : 'No'}
            dataId="hide-emails"
          />
        </>
      ) : null}
      {membership?.auth_type === 'password' ? (
        <>
          <Divider />
          <PropertyListItem label="Password" value={membership.password || ''} />
        </>
      ) : null}
      {
        // checking if expiration type can be cast to a number is a temporary fix--should only be null or seconds
        membership?.auth_expiration ? (
          <>
            <Divider />
            <PropertyListItem
              label="Authorization Expiration"
              value={
                Number(membership.auth_expiration)
                  ? timeForHumans(membership.auth_expiration)
                  : membership.auth_expiration
              }
            />
          </>
        ) : null
      }
      {systemUsesWallets ? (
        <>
          <Divider />
          <PropertyListItem
            label="Minimum Wallet Balance"
            value={minimum_wallet_deposit ?? '(not set)'}
            dataId="min-wallet-balance"
          />
        </>
      ) : null}
      {currentSystem.billing ? (
        <>
          <Divider />
          <PropertyListItem
            label="Credit Card Hold"
            value={credit_card_hold ?? '(not set)'}
            dataId="cc-hold"
          />
        </>
      ) : null}
      <Divider />
      <PropertyListItem
        divider
        label="Priority"
        value={membership?.priority}
        dataId="priority"
      />
      <PropertyListItem
        divider
        label="Max Simultaneous Rentals"
        value={membership?.max_rentals ?? '(not set)'}
        dataId="max-rentals"
      />
      <PropertyListItem
        divider
        label="Hidden"
        value={membership?.hidden ? 'Yes' : 'No'}
        dataId="membership-hidden"
      />
      <PropertyListItem divider label="Branding Color" dataId="branding-color">
        <div className="flexDisplay">
          <div
            className="membershipBrandingColor"
            style={{ backgroundColor: membership?.color_primary }}
          />
          <div>{membership?.color_primary}</div>
        </div>
      </PropertyListItem>

      <PropertyListItem dataId="logo" label="Logo Image">
        <div id="currentLogo">
          {logo ? (
            <div className="logoContainer">
              {!logo.approved_on && !logo.rejected ? (
                <i className="fa fa-hourglass-half fa-fw navIconStyle navIcon pull-right  logoIcon" />
              ) : logo.rejected ? (
                <i className="fa fa-ban fa-fw navIconStyle navIcon pull-right imageReject logoIcon" />
              ) : logo.approved_on ? (
                <i className="fa fa-check-circle fa-fw navIconStyle navIcon pull-right  imageApprove logoIcon">
                  {' '}
                </i>
              ) : null}
              <img
                alt=""
                className="logoRadius"
                src={`data:image/png;base64,${logo.image}`}
              />
              <div className="logoOverlay">
                <div className="imageText">
                  {!logo.approved_on && !logo.rejected
                    ? 'Image is being reviewed by Movatic.'
                    : logo.rejected
                    ? 'Image has been rejected, due to poor quality or due to cropping issues.'
                    : logo.approved_on
                    ? 'Image has been approved.'
                    : null}
                </div>
              </div>
            </div>
          ) : (
            <NoFilePlaceHolder />
          )}
        </div>
      </PropertyListItem>
    </PropertyList>
  );
}

export default MembershipCardDetails;
